import { Cancel } from '@mui/icons-material';
import {
  Button,
  Divider,
  Drawer,
  FormControl,
  FormLabel,
  Grid2,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Article, Unit } from '@thro-types/billing';

import { Field, Form, Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useArticleMutation } from 'api/hooks/billing/articles';
import { useGetSalesTaxesListQuery } from 'api/hooks/billing/tax';
import { SaveButton } from 'app/components';
import { CurrencyFieldText } from 'app/components/CurrencyTextField';
import { getTaxOptionsArray } from 'app/containers/Billing/components/helper';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { articleSettingsInitialValue, articleSettingsValidationSchema } from './validation';

interface ArticleProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  article: Article | undefined;
}

export const ArticleDrawer = (props: ArticleProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { update } = useArticleMutation();
  const { data: salesTaxes } = useGetSalesTaxesListQuery();

  const handleSaveClick = async values => {
    try {
      if (!props.article || update.isPending) {
        return;
      }

      delete values.id;

      await update.mutateAsync({
        sku: props.article.sku,
        data: { ...values, price: Number(values.price) }
      });
      dispatch(
        AlertActions.success(t(translations.pages.billingSettings.mutation.articleUpdateSuccess()))
      );
      props.setOpen(false);
    } catch (error) {
      dispatch(
        AlertActions.error(t(translations.pages.billingSettings.mutation.articleUpdateFailed()))
      );
    }
  };

  if (!props.article) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={props.open}
      PaperProps={{
        sx: { width: { xs: '70%', md: '35%', xl: '25%' } }
      }}
      onClose={() => props.setOpen(false)}
    >
      <Formik
        initialValues={articleSettingsInitialValue(props.article)}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={articleSettingsValidationSchema(t)}
        validateOnMount
      >
        {({ touched, errors, values, setFieldValue, isValid }) => (
          <Form>
            <Grid2
              container
              padding={2}
              flexDirection={'column'}
              height={'100vh'}
              justifyContent={'flex-end'}
              gap={2}
              width={'100%'}
            >
              <Grid2 container size={12} gap={1} flexDirection={'column'}>
                <Typography variant="h5" fontWeight={'bold'}>
                  {t(translations.pages.billingSettings.article.drawer.update())}
                </Typography>
                <Divider />
              </Grid2>
              <Grid2 container direction={'column'} gap={2}>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.name())}</FormLabel>
                  <Field
                    as={TextField}
                    id="name"
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    size="small"
                    inputProps={{ maxLength: 50 }}
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.description())}</FormLabel>
                  <Field
                    as={TextField}
                    id="description"
                    name="description"
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    size="small"
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.price())}</FormLabel>
                  <CurrencyFieldText
                    value={values.price}
                    onChange={e => setFieldValue('price', e)}
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.unit())}</FormLabel>
                  <Select
                    size="small"
                    name="unit"
                    id="unit"
                    value={values.unit}
                    onChange={e => setFieldValue('unit', e.target.value)}
                  >
                    {Object.values(Unit).map(unit => (
                      <MenuItem value={unit} key={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.tax())}</FormLabel>
                  <Select
                    size="small"
                    value={values.defaultTaxId}
                    onChange={e => setFieldValue('defaultTaxId', e.target.value)}
                  >
                    {getTaxOptionsArray(
                      salesTaxes.data,
                      //add salesTax only when it is not the current version
                      !props.article?.defaultTax?.isCurrentVersion ? props.article : undefined
                    ).map(tax => (
                      <MenuItem value={tax?.id} key={tax?.id} style={{ fontSize: 14 }}>
                        {`${tax?.key} - ${tax?.text}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={12} alignContent={'center'} marginTop={'auto'}>
                <Divider sx={{ marginLeft: 1, marginRight: 0.6, flex: 1 }} />
              </Grid2>
              <Grid2 container gap={2} justifyContent={'flex-end'} alignItems={'flex-end'}>
                <Button
                  variant="contained"
                  startIcon={<Cancel />}
                  color="error"
                  onClick={() => props.setOpen(false)}
                  disabled={update.isPending}
                >
                  {t(translations.common.cancel())}
                </Button>
                <SaveButton isSubmitting={update.isPending} isValid={isValid} />
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};
